import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import SendComposeModal from './SendComposeModal';
import { apiErrorText, apiRequest } from '../../utils';
import './GuestList.css';

/* Who is invited to an invite-only event.

   ★ It lives on the PEOPLE screen, not the editor. The editor is behind the gate that admits
   the EXTERNAL host — the facilitator we send in, and the person that gate exists to keep away
   from the attendance list. Putting the guest list there would hand them exactly what was
   withheld.

   ★ Adding somebody does NOT tell them. Building a list a week before announcing anything is
   the normal case, so the invitation stays a separate press on the compose modal. The pill on
   each row says whether they have actually been told yet — and the button under the list is
   that second press, HERE, where the pill is. It used to be nowhere: publishing invited whoever
   was on the list at that moment, and everybody added afterwards kept a "not told yet" pill
   with nothing on the screen that could change it.

   ★ Somebody who has already answered cannot be ticked. A second invitation reads to them as
   though their answer went missing — and the send re-resolves its audience through the
   ``not_registered`` segment, so ticking them would also make the headcount in the composer
   disagree with the list on screen.

   ★ Tags SELECT, they do not RULE. Picking "antwerp" adds the people who carry that tag today
   and records the tag as the reason. It is not re-evaluated — roster tags live in a blob that a
   CSV re-upload replaces wholesale, so a live rule would let somebody fixing a spreadsheet
   silently un-invite people from next Thursday's townhall. New joiners are handled by pressing
   the tag again, which only ever adds. */

const GuestList = ({ eventId, onChanged }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [picked, setPicked] = useState([]);
  const [search, setSearch] = useState('');
  // Who the invitation is about to go to, and whether the composer is open for them.
  const [toTell, setToTell] = useState([]);
  const [composing, setComposing] = useState(false);

  const load = useCallback(() => {
    apiRequest('GET', `/company/event/${eventId}/invites`)
      .then(setData)
      .catch((err) => setNotice(apiErrorText(t, err)));
  }, [eventId, t]);

  useEffect(() => { load(); }, [load]);

  const invited = useMemo(() => (data && data.invited) || [], [data]);
  const invitedIds = useMemo(() => new Set(invited.map((p) => p.partner_id)), [invited]);
  // Invitable = on the list, not told yet, and has not answered under their own steam (an
  // invite-only event's card does reach its guests' feed, so somebody can be registered
  // without ever having been mailed).
  const canTell = useMemo(
    () => invited.filter((p) => !p.notified && !p.registered), [invited],
  );
  // Ticks survive a reload of the list, but only for people still worth telling — the ones
  // just invited drop out on their own.
  const tellIds = useMemo(() => {
    const open = new Set(canTell.map((p) => p.partner_id));
    return toTell.filter((id) => open.has(id));
  }, [toTell, canTell]);
  /* ★ Ticking NOTHING means everyone still waiting. A tick NARROWS — exactly what `ids` does to
     a segment on the send itself — rather than being the thing that arms the button. The first
     cut had two presses, "tick them" then "invite", and the second read "Invite 0" and did
     nothing until you had found the first: a button that needs another button is a button that
     does nothing.
     Capped at what one send can carry, because the route REFUSES above its ceiling rather than
     truncating — the cap has to bite before the host writes the message, not after. */
  const willTell = useMemo(() => {
    const chosen = tellIds.length ? tellIds : canTell.map((p) => p.partner_id);
    const max = (data && data.invite_max) || chosen.length;
    return chosen.slice(0, max);
  }, [tellIds, canTell, data]);
  const capped = (tellIds.length || canTell.length) > willTell.length;

  // Everybody not already on the list, narrowed by the search box.
  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ((data && data.roster) || [])
      .filter((p) => !invitedIds.has(p.id))
      .filter((p) => !q || (p.name || '').toLowerCase().includes(q)
        || (p.email || '').toLowerCase().includes(q));
  }, [data, invitedIds, search]);

  const act = (promise, done) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    promise
      .then((res) => { done(res); if (onChanged) onChanged(); })
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  const add = (body, label) => act(
    apiRequest('POST', `/company/event/${eventId}/invites`, body),
    (res) => {
      setData((d) => ({ ...d, invited: res.invited }));
      setPicked([]);
      setNotice(label(res));
    },
  );

  const remove = (partnerId) => act(
    apiRequest('DELETE', `/company/event/${eventId}/invites/${partnerId}`),
    (res) => setData((d) => ({ ...d, invited: res.invited })),
  );

  const toggle = (id) => setPicked((list) =>
    (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  const toggleTell = (id) => setToTell((list) =>
    (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));


  /* The invitation went out. The pills flip on the reload, so the people just told fall out of
     the tickable set by themselves rather than being crossed off by hand. */
  const told = (res) => {
    setNotice(t('invitesSent', { n: (res && res.sent) || 0 }));
    setToTell([]);
    load();
    if (onChanged) onChanged();
  };

  if (data === null) return <span className="auth__spinner" />;

  if (!data.is_invite_only) {
    return <p className="ce-note">{t('guestListNotInviteOnly')}</p>;
  }

  return (
    <div className="gl">
      {/* ★ The same composer every other send on these screens goes through, and the same
          `event_invite` message publishing sends — a second, near-identical invitation so this
          screen could have its own wording is how a company ends up with two that disagree. */}
      {composing && (
        <SendComposeModal
          eventId={eventId}
          kind="invite"
          segment="not_registered"
          ids={willTell}
          sendPath={`/company/event/${eventId}/invite`}
          title={t('guestInviteThem')}
          onClose={() => setComposing(false)}
          onSent={told}
        />
      )}
      <p className="bravos-q__hint">{t('guestListHint')}</p>

      {/* On the list, with why they are and whether they have been told. The tick box is on
          the rows the invitation can still go to; everyone else keeps their pill and no box,
          which is the difference between "not offered" and "unticked". */}
      <p className="bravos-cal__label">{t('guestListInvited', { n: invited.length })}</p>
      {invited.length === 0 && <p className="ce-note">{t('guestListEmpty')}</p>}
      {invited.map((p) => {
        const tellable = !p.notified && !p.registered;
        return (
          <div key={p.partner_id} className="ce-person">
            {tellable ? (
              <input type="checkbox" className="gl-tick" checked={tellIds.includes(p.partner_id)}
                     aria-label={p.name || p.email} disabled={busy}
                     onChange={() => toggleTell(p.partner_id)} />
            ) : <span className="gl-tick gl-tick--empty" />}
            <span className="ce-person__body">
              <span className="ce-person__name">{p.name || p.email}</span>
              <span className="ce-person__email">{p.email}</span>
            </span>
            {p.source === 'tag' && p.source_tag && (
              <span className="ce-pill gl-why">{p.source_tag}</span>
            )}
            {/* Three states, not two: told, coming (they answered — nothing left to send), and
                still waiting to be told. */}
            <span className={`ce-pill ${p.registered || p.notified
              ? 'ce-pill--going' : 'ce-pill--draft'}`}>
              {p.registered ? t('segment_registered')
                : (p.notified ? t('guestNotified') : t('guestNotTold'))}
            </span>
            <button type="button" className="ce-person__act" disabled={busy}
                    onClick={() => remove(p.partner_id)}>
              {t('remove')}
            </button>
          </div>
        );
      })}

      {/* Telling them. Separate from adding them, and it is the press this screen was missing:
          the "not told yet" pill had nothing anywhere that could change it once the event had
          been published. */}
      {canTell.length > 0 && data.can_manage && (
        <>
          {/* ★ Before the button, not under it. The send refuses on an event nobody has been
              told about — and on an invite-only event the answer is not "come back later" but
              "press publish", which invites this whole list in one go. Saying that here is the
              difference between a disabled button and a next step. */}
          {!data.can_send && (
            <p className="ce-note gl-blocked">
              {t('guestPublishFirst')}{' '}
              <button type="button" className="gl-link"
                      onClick={() => navigate(`/host-event/${eventId}`)}>
                {t('guestPublishFirstLink')}
              </button>
            </p>
          )}
          {capped && <p className="ce-note">{t('guestInviteCapped', { n: willTell.length })}</p>}
          <div className="bravos-form__row mt-2">
            <button type="button" className="bravos-copy"
                    disabled={busy || !willTell.length || !data.can_send}
                    onClick={() => setComposing(true)}>
              <Icon name="paper-plane" />{' '}
              {tellIds.length ? t('guestInviteSelected', { n: willTell.length })
                : t('guestInviteWaiting', { n: willTell.length })}
            </button>
            {tellIds.length > 0 && (
              <button type="button" className="bravos-copy" disabled={busy}
                      onClick={() => setToTell([])}>
                {t('guestInviteClearPicks')}
              </button>
            )}
          </div>
        </>
      )}

      {/* Add a whole tag at once. One press, and it only ever adds — pressing it again next
          month picks up whoever has been tagged since. */}
      {(data.all_tags || []).length > 0 && (
        <>
          <p className="bravos-cal__label mt-3">{t('guestListByTag')}</p>
          <div className="ce-tabs">
            {data.all_tags.map((tag) => (
              <button key={tag} type="button" className="ce-tab" disabled={busy}
                      onClick={() => add({ tags: [tag] },
                        (r) => t('guestListAdded', { n: r.added }))}>
                <Icon name="plus" /> {tag}
              </button>
            ))}
          </div>
        </>
      )}

      <p className="bravos-cal__label mt-3">{t('guestListAddPeople')}</p>
      <input type="text" className="gl-search" value={search} maxLength={80}
             placeholder={t('searchPeople')}
             onChange={(e) => setSearch(e.target.value)} />
      <div className="gl-candidates">
        {candidates.length === 0 && <p className="ce-note">{t('guestListAllAdded')}</p>}
        {candidates.map((p) => (
          <label key={p.id} className="ce-check">
            <input type="checkbox" checked={picked.includes(p.id)}
                   onChange={() => toggle(p.id)} />
            <span>
              {p.name || p.email}
              <span className="ce-check__hint">{p.email}{
                (p.tags || []).length ? ` · ${p.tags.join(', ')}` : ''}</span>
            </span>
          </label>
        ))}
      </div>
      <button type="button" className="bravos-submit mt-2"
              disabled={busy || picked.length === 0}
              onClick={() => add({ ids: picked }, (r) => t('guestListAdded', { n: r.added }))}>
        {t('guestListAddSelected', { n: picked.length })}
      </button>

      {notice && <p className="bravos-cal__notice">{notice}</p>}
    </div>
  );
};

export default GuestList;
